/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, Lock } from 'lucide-react';
import { getMyConsents, updateConsents } from '@/lib/security';

export const ConsentCenter = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await getMyConsents();
      setItems(r.consents || []);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
         
        console.debug('[ConsentCenter] getMyConsents failed', e);
      }
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    setItems(items.map((it) => (it.id === id && !it.required) ? { ...it, granted: !it.granted, _dirty: true } : it));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = items.filter((it) => it._dirty).map((it) => ({ consent_id: it.id, granted: !!it.granted }));
      if (payload.length === 0) { toast.info(t('consentNoChanges')); setSaving(false); return; }
      await updateConsents(payload);
      toast.success(t('consentSaved'));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('genericError')); }
    finally { setSaving(false); }
  };

  const hasChanges = items.some((it) => it._dirty);

  return (
    <Card data-testid="consent-center-card">
      <CardHeader>
        <CardTitle className="font-display flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> {t('consentTitle')}</CardTitle>
        <CardDescription>{t('consentSubtitle')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.map((it) => (
          <div key={it.id} className="flex items-start justify-between gap-3 rounded-2xl border border-border/60 p-3" data-testid={`consent-row-${it.id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold text-sm">{it.title}</div>
                {it.required && <Badge variant="secondary" className="text-[9px] uppercase"><Lock className="h-2.5 w-2.5 mr-1" />{t('consentRequired')}</Badge>}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{it.desc}</p>
              {it.updated_at && <p className="text-[10px] text-muted-foreground/70 mt-1">{t('consentLastUpdated', { date: new Date(it.updated_at).toLocaleDateString('it') })}</p>}
            </div>
            <div className="flex-shrink-0 pt-1">
              <Switch checked={!!it.granted} disabled={it.required} onCheckedChange={() => toggle(it.id)} data-testid={`consent-switch-${it.id}`} />
            </div>
          </div>
        ))}
        {hasChanges && (
          <div className="flex justify-end pt-2">
            <Button onClick={save} disabled={saving} data-testid="consent-save-btn">{saving ? t('processing') : t('save')}</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ConsentCenter;
